/**
 * One pane's market profile.
 *
 * Fetched rather than streamed. A profile is a shape over a whole window —
 * a session, a day, a week — and one print moves it by less than a pixel, so
 * subscribing to the tape to redraw it would spend the socket on nothing a
 * reader could see. It refreshes when the trader changes what they are asking
 * for, and on demand.
 *
 * Instantiable per pane for the same reason `useChartData` is: two profile
 * panels on two symbols is an ordinary thing to want.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { api, type ProfileResponse } from './api.js';

export type ProfilePeriod = 'day' | 'session' | 'week' | 'range';

export interface ProfileOptions {
  readonly symbol: string;
  readonly timeframe: string;
  readonly period: ProfilePeriod;
  /** Epoch seconds, for `period: 'range'`. */
  readonly from?: number;
  readonly to?: number;
  readonly periodMinutes?: number;
  /** Read through a ref, so a fresh closure every render costs nothing. */
  readonly onError?: (message: string) => void;
}

export interface ProfileState {
  readonly data: ProfileResponse | null;
  readonly loading: boolean;
  readonly reload: () => void;
}

export function useProfile(options: ProfileOptions): ProfileState {
  const { symbol, timeframe, period, from, to, periodMinutes } = options;
  const [data, setData] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);

  const onError = useRef(options.onError);
  useEffect(() => { onError.current = options.onError; });

  useEffect(() => {
    // Empty for the render between mount and whatever resolves a default.
    if (!symbol) return undefined;
    // A range with no bounds is not a range. Asking anyway returns the whole
    // series under a label that says otherwise.
    if (period === 'range' && (from === undefined || to === undefined)) return undefined;

    let cancelled = false;
    /*
      Cleared before the request, not after it.

      A profile is read as a picture of ONE window. Leaving the previous
      symbol's shape on screen while the new one loads is the same failure the
      chart's blanking prevents — except worse, because a profile carries no
      candles to make the mismatch obvious.
    */
    setData(null);
    setLoading(true);

    void (async () => {
      try {
        const res = await api.profile(symbol, {
          timeframe,
          period,
          ...(from !== undefined ? { from } : {}),
          ...(to !== undefined ? { to } : {}),
          ...(periodMinutes !== undefined ? { periodMinutes } : {}),
        });
        if (!cancelled) setData(res);
      } catch (e) {
        if (!cancelled) {
          onError.current?.(e instanceof Error ? e.message : 'Failed to load the profile.');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [symbol, timeframe, period, from, to, periodMinutes, nonce]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return { data, loading, reload };
}
